import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Zap, CheckCircle2 } from "lucide-react";

export default function NewsletterCTA() {
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    api.get("/subscribers/count")
      .then((r) => setCount(r.data.count))
      .catch(() => {});
    if (localStorage.getItem("gawx_subscribed") === "1") setDone(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (zip && !/^\d{5}$/.test(zip)) {
      toast.error("ZIP must be 5 digits (leave blank if unsure)");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/subscribers", { email, zip });
      localStorage.setItem("gawx_subscribed", "1");
      setDone(true);
      if (data.already_subscribed) {
        toast.info("You're already on the list — thanks!");
      } else {
        toast.success("You're in. Watch for severe weather alerts.");
        setCount((c) => (typeof c === "number" ? c + 1 : c));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg || "").join(" ")
        : detail || "Signup failed. Try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="newsletter-cta"
      className="relative border-y border-white/10 bg-[#151b26]/60 py-12"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#d90429]/40 bg-[#d90429]/10 px-3 py-1">
            <Zap className="h-3.5 w-3.5 text-[#d90429]" />
            <span className="font-oswald text-[10px] font-bold uppercase tracking-[0.3em] text-[#ff6b7a]">
              Severe Weather Alerts
            </span>
          </div>
          <h2 className="mt-3 font-oswald text-2xl font-bold uppercase tracking-wider text-white sm:text-3xl lg:text-4xl">
            Get Georgia Alerts <span className="text-[#d90429]">In Your Inbox</span>
          </h2>
          <p className="mt-2 text-sm text-gray-300">
            Tornado warnings, severe thunderstorm watches, tropical system tracks, winter storm advisories — delivered when it matters most. Add your ZIP for local‑first alerts.
          </p>
          {typeof count === "number" && count > 0 && (
            <div className="mt-3 font-mono text-xs uppercase tracking-widest text-gray-400">
              {count.toLocaleString()} Georgian{count === 1 ? "" : "s"} already signed up
            </div>
          )}
        </div>

        {done ? (
          <div
            data-testid="newsletter-success"
            className="glass flex w-full max-w-md items-center gap-3 rounded-sm border-white/15 p-5"
          >
            <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-400" />
            <div>
              <div className="font-oswald text-base font-bold uppercase tracking-wider text-white">
                You're on the list
              </div>
              <div className="text-sm text-gray-300">
                We'll ping you the moment severe weather threatens your area.
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="glass w-full max-w-md rounded-sm border-white/15 p-5"
          >
            <label className="mb-2 flex items-center gap-2 font-oswald text-[11px] uppercase tracking-widest text-gray-400">
              <Mail className="h-3.5 w-3.5" /> Email Address
            </label>
            <input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@peachstate.com"
              required
              className="w-full rounded-sm border border-white/15 bg-black/60 px-3 py-2 text-sm text-white outline-none focus:border-[#d90429]"
            />

            <label className="mb-2 mt-4 block font-oswald text-[11px] uppercase tracking-widest text-gray-400">
              Georgia ZIP (optional)
            </label>
            <input
              data-testid="newsletter-zip"
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
              placeholder="30303"
              className="w-full rounded-sm border border-white/15 bg-black/60 px-3 py-2 text-sm text-white outline-none focus:border-[#d90429]"
            />

            <button
              type="submit"
              data-testid="newsletter-submit"
              disabled={busy}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-sm bg-[#d90429] px-4 py-3 font-oswald text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#ef233c] disabled:opacity-60"
            >
              {busy ? "Signing up…" : "Get Severe Weather Alerts"}
            </button>

            <div className="mt-2 text-center text-[11px] text-gray-500">
              No spam. Unsubscribe any time.
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
